import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;

/**
 * Miami's hero board (2026-10-17), after the theme's own photograph (cover.webp): Biscayne Bay at
 * sunset, synthwave. A sky going from deep plum through flamingo pink to the horizon's orange, level
 * cloud bands lit from below, the first stars; a great synthwave sun behind the city, its gaps drifting
 * down and mirrored in the bay; the towers (a new skyline each visit, the tall ones always in the
 * middle) in Miami's deepened pastels, in perspective, backlit, their windows lit golden, some in neon;
 * the land under them; the water waving with the reflections; at the left the beach with its lapping
 * waves, dune grass and two palms, their crowns swaying. The counter drives the sun (owner): its gaps
 * run quicker and wider and its glow swells, and the neon towers' signs and windows change colour
 * together. The page's wallpaper is Dock Town's pelicans. Sized by the stage's height.
 */
@Component( {
  selector: 'app-hero-miami',
  template: '<canvas #canvas class="hmia-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.hmia-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroMiamiComponent extends HeroMotifBase {

  private back: HTMLCanvasElement | null = null;
  private front: HTMLCanvasElement | null = null;
  private skyC: HTMLCanvasElement | null = null;
  private sunDisc: HTMLCanvasElement | null = null;
  private sunTmp: HTMLCanvasElement | null = null;
  private refl: HTMLCanvasElement | null = null;
  /** The palms' crowns on layers of their own, so they can sway about the top of their trunks. */
  private crowns: { c: HTMLCanvasElement; px: number; py: number; phase: number }[] = [];
  private sunGeo = { x: 0, y: 0, r: 0, scale: 1 };
  private sunPhase = 0;
  private beachAt: ( x: number ) => number = () => 0;
  private beachEnd = 0;
  private over: HTMLCanvasElement | null = null;
  private landFront: HTMLCanvasElement | null = null;
  /** Each neon tower's strips; when the counter counts they flicker at random. */
  private neons: { rects: [ number, number, number, number ][]; wins: [ number, number, number, number ][]; colour: RGB; from: RGB; to: RGB; fade: number; next: number }[] = [];
  /** Picked once per visit, so the skyline is new each time but keeps its shape on a resize. */
  private readonly citySeed = 1 + Math.floor( Math.random() * 100000 );
  private sunFrame: HTMLCanvasElement | null = null;
  private scaleK = 1;
  private UW = 0;
  private horizon = 0;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.2;
  }

  protected colorsChanged(): void {
    this.back = null;
    this.front = null;
  }

  protected build(): void {
    const H = this.height;
    this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    this.UW = H * 1.75;
    this.horizon = H * 0.68;
    this.back = null;
    this.front = null;
  }

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    if ( this.frozen ) {
      // the page's wallpaper: the pelicans alone, still, all down the page
      this.drawWallPelicans( ctx );
      return;
    }
    if ( !this.back ) { this.bake( ctx.getTransform().a ); }
    if ( !this.back || !this.front ) { return; }
    if ( this.skyC ) { ctx.drawImage( this.skyC, 0, 0, W, H ); }
    this.drawSun( ctx, dt );
    ctx.drawImage( this.back, 0, 0, W, H );
    this.drawNeon( ctx, dt );
    if ( this.landFront ) { ctx.drawImage( this.landFront, 0, 0, W, H ); }
    this.drawSunReflection( ctx );
    this.drawWaves( ctx );
    ctx.drawImage( this.front, 0, 0, W, H );
    this.drawLapping( ctx );
    if ( this.over ) { ctx.drawImage( this.over, 0, 0, W, H ); }
    this.drawCrowns( ctx );
  }

  /**
   * The synthwave sun, alive (owner: the counter drives it): its level gaps drift slowly down across its
   * lower half and widen as they go; when the counter counts they run quicker and wider and its glow
   * swells.
   */
  private drawSun( ctx: CanvasRenderingContext2D, dt: number ): void {
    if ( !this.sunDisc ) { return; }
    const { x, y, r, scale } = this.sunGeo;
    const e = this.energy;
    if ( dt > 0 ) { this.sunPhase = ( this.sunPhase + dt * ( 0.08 + 0.7 * e ) ) % 1; }
    const [ , pink, , , gold ] = this.five();
    // the glow swelling with the counter
    if ( e > 0.01 ) {
      const gl = ctx.createRadialGradient( x, y, r * 0.8, x, y, r * 2.6 );
      gl.addColorStop( 0, this.rgba( this.mix( gold, pink, 0.4 ), 0.35 * e ) );
      gl.addColorStop( 1, this.rgba( pink, 0 ) );
      ctx.fillStyle = gl;
      ctx.fillRect( x - r * 2.6, y - r * 2.6, r * 5.2, r * 5.2 );
    }
    const side = Math.ceil( ( r * 2 + 8 ) * scale );
    if ( !this.sunTmp ) { this.sunTmp = document.createElement( 'canvas' ); }
    const t = this.sunTmp;
    if ( t.width !== side ) { t.width = side; t.height = side; }
    const tg = t.getContext( '2d' )!;
    tg.setTransform( 1, 0, 0, 1, 0, 0 );
    tg.clearRect( 0, 0, side, side );
    const sx0 = ( x - r - 4 ) * scale, sy0 = ( y - r - 4 ) * scale;
    tg.drawImage( this.sunDisc, sx0, sy0, side, side, 0, 0, side, side );
    tg.setTransform( scale, 0, 0, scale, -sx0, -sy0 );
    const n = 8;
    for ( let i = -1; i < n; i++ ) {
      const f = ( i + this.sunPhase ) / n;
      if ( f < 0 ) { continue; }
      const gy = y - r * 0.05 + r * f * 1.1;
      const gh = r * ( 0.012 + 0.05 * f ) * ( 1 + 0.8 * e );
      tg.clearRect( x - r - 4, gy, r * 2 + 8, gh );
    }
    ctx.drawImage( t, x - r - 4, y - r - 4, side / scale, side / scale );
    this.sunFrame = t;
  }

  /**
   * The synthwave sun mirrored in the bay (owner): its striped disc reflected below the horizon,
   * squashed, softer, its gaps moving with the sun's, and the whole of it waving with the water.
   */
  private drawSunReflection( ctx: CanvasRenderingContext2D ): void {
    const t = this.sunFrame;
    if ( !t ) { return; }
    const { x, y, r, scale } = this.sunGeo;
    const hz = this.horizon;
    const H = this.height;
    const k = this.scaleK;
    const tt = this.time * W0;
    const squash = 0.55;
    const top = y - r - 4;
    ctx.save();
    ctx.globalAlpha = 0.42;
    for ( let yy = Math.floor( hz ); yy < H; yy += 2 ) {
      const ys = hz - ( yy - hz ) / squash;
      if ( ys < top || ys > y + r + 4 ) { continue; }
      const d = ( yy - hz ) / ( H - hz );
      const off = ( Math.sin( yy * 0.11 - tt * 6 ) * 3 + Math.sin( yy * 0.043 + tt * 3 ) * 2 ) * k * ( 0.3 + d ) * 1.6;
      ctx.drawImage( t, 0, ( ys - top ) * scale, t.width, Math.max( 1, 2 / squash * scale ), x - r - 4 + off, yy, t.width / scale, 2 );
    }
    ctx.restore();
  }

  /** The reflections on the water, gently waving: drawn in thin level slices, each pushed side to side. */
  private drawWaves( ctx: CanvasRenderingContext2D ): void {
    if ( !this.refl ) { return; }
    const W = this.width;
    const H = this.height;
    const hz = this.horizon;
    const k = this.scaleK;
    const sc = this.sunGeo.scale;
    const t = this.time * W0;
    for ( let y = Math.floor( hz ); y < H; y += 2 ) {
      const d = ( y - hz ) / ( H - hz );
      const off = ( Math.sin( y * 0.11 - t * 6 ) * 3 + Math.sin( y * 0.043 + t * 3 ) * 2 ) * k * ( 0.3 + d );
      ctx.drawImage( this.refl, 0, y * sc, W * sc, 2 * sc, off, y, W, 2 );
    }
  }

  /**
   * The little waves lapping the beach at the left (owner): a thin sheet of water running up the sand
   * and sliding back, its edge of foam broken into bubbles, two waves out of step.
   */
  private drawLapping( ctx: CanvasRenderingContext2D ): void {
    if ( !this.beachEnd ) { return; }
    const k = this.scaleK;
    const t = this.time * W0;
    const [ , pink, coral ] = this.five();
    const by = this.beachAt;
    const end = this.beachEnd;
    for ( const [ ph, reach ] of [ [ 0, 9 ], [ Math.PI, 6 ] ] ) {
      const s = 0.5 + 0.5 * Math.sin( t * 5 + ph );
      const o = s * reach * k;
      const alpha = 0.25 + 0.5 * s;
      const edge = ( x: number ) => by( x ) + o + Math.sin( x * 0.06 + t * 5 + ph ) * 1.5 * k;
      // the sheet of water
      ctx.fillStyle = this.rgba( this.mix( pink, coral, 0.4 ), 0.22 * alpha );
      ctx.beginPath();
      for ( let x = -10; x <= end; x += 3 ) { x <= -10 ? ctx.moveTo( x, by( x ) ) : ctx.lineTo( x, by( x ) ); }
      for ( let x = end; x >= -10; x -= 3 ) { ctx.lineTo( x, edge( x ) ); }
      ctx.closePath();
      ctx.fill();
      // its foam edge
      ctx.strokeStyle = this.rgba( this.mix( pink, [ 255, 255, 255 ], 0.5 ), 0.6 * alpha );
      ctx.lineWidth = Math.max( 0.8, 1.2 * k );
      ctx.beginPath();
      for ( let x = -10; x <= end; x += 3 ) { x <= -10 ? ctx.moveTo( x, edge( x ) ) : ctx.lineTo( x, edge( x ) ); }
      ctx.stroke();
      ctx.fillStyle = this.rgba( this.mix( pink, [ 255, 255, 255 ], 0.55 ), 0.5 * alpha );
      for ( let x = -10; x <= end; x += 4 * k ) {
        const b = Math.sin( x * 12.9898 ) * 43758.5453;
        if ( b - Math.floor( b ) < 0.45 ) { ctx.beginPath(); ctx.arc( x, edge( x ) + 1.2 * k, 0.7 * k, 0, Math.PI * 2 ); ctx.fill(); }
      }
    }
  }

  /**
   * The towers' neon signs and their lit windows. At rest each keeps its own colour; when the counter
   * counts, each tower's sign and windows pass together from one neon colour to another at their own
   * random moments (owner), the change soft, not a blink.
   */
  private drawNeon( ctx: CanvasRenderingContext2D, dt: number ): void {
    const k = this.scaleK;
    const e = this.energy;
    const now = performance.now() / 1000;
    const [ , pink, , teal, gold ] = this.five();
    const choices: RGB[] = [ pink, teal, [ 146, 181, 232 ], gold ];
    for ( const n of this.neons ) {
      if ( dt > 0 ) {
        if ( e > 0.05 && now >= n.next ) {
          n.from = n.colour;
          const others = choices.filter( c => c !== n.to );
          n.to = others[ Math.floor( Math.random() * others.length ) ];
          n.fade = 0;
          n.next = now + 1.5 + Math.random() * ( 4 - 2 * e );
        }
        n.fade = Math.min( 1, n.fade + dt / 0.8 );
        n.colour = this.mix( n.from, n.to, n.fade );
      }
      // the windows, without glow
      ctx.fillStyle = this.rgba( this.mix( n.colour, [ 255, 255, 255 ], 0.2 ), 0.88 );
      for ( const [ x, y, w, h ] of n.wins ) { ctx.fillRect( x, y, w, h ); }
    }
    ctx.save();
    ctx.shadowBlur = 7 * k;
    for ( const n of this.neons ) {
      ctx.shadowColor = this.rgba( n.colour, 0.95 );
      ctx.fillStyle = this.rgba( this.mix( n.colour, [ 255, 255, 255 ], 0.2 ), 1 );
      for ( const [ x, y, w, h ] of n.rects ) { ctx.fillRect( x, y, w, h ); }
    }
    ctx.restore();
  }

  /** The palms' crowns, swaying a little about the tops of their trunks. */
  private drawCrowns( ctx: CanvasRenderingContext2D ): void {
    const W = this.width;
    const H = this.height;
    const t = this.time * W0;
    for ( const c of this.crowns ) {
      // swaying more in the wind (owner), leaning on with it a little
      const a = 0.035 + 0.04 * Math.sin( t * 3 + c.phase ) + 0.015 * Math.sin( t * 7 + c.phase * 2 );
      ctx.save();
      ctx.translate( c.px, c.py );
      ctx.rotate( a );
      ctx.translate( -c.px, -c.py );
      ctx.drawImage( c.c, 0, 0, W, H );
      ctx.restore();
    }
  }

  /**
   * One brown pelican gliding, facing right (y down), as Dock Town draws it, backlit by the sunset: long
   * broad wings in two parts (the arm to the bend at `arm`, the hand to the tip at `hand`; -1 raised, 0 out,
   * 1 lowered) with their silvery leading edge and dark fingertips, the short tail, the body, the pale
   * head on the shoulders, the long bill with its pouch.
   */
  private pelican( ctx: CanvasRenderingContext2D, s: number, arm: number, hand: number ): void {
    const dusk: RGB = [ 60, 30, 46 ];
    const tone = ( c: number[] ) => this.rgba( this.mix( c as RGB, dusk, 0.4 ), 1 );
    const brown = [ 154, 145, 131 ], silver = [ 216, 210, 198 ], dark = [ 61, 56, 51 ], head = [ 246, 242, 232 ], bill = [ 194, 165, 116 ], pouch = [ 94, 82, 69 ];
    const pose = ( a: number[], t: number ) => t <= 0 ? a[ 1 ] + ( a[ 0 ] - a[ 1 ] ) * -t : a[ 1 ] + ( a[ 2 ] - a[ 1 ] ) * t;
    const bendY = pose( [ -0.3, -0.18, 0.05 ], arm );
    const tipY = pose( [ -0.6, -0.1, 0.34 ], hand ), tipX = pose( [ 1.0, 1.14, 1.04 ], hand );
    const fill = ( pts: number[][], c: string ) => {
      ctx.fillStyle = c;
      ctx.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? ctx.lineTo( x * s, y * s ) : ctx.moveTo( x * s, y * s ) );
      ctx.closePath();
      ctx.fill();
    };
    const to = ( a: number[], b: number[], t: number ) => [ a[ 0 ] + ( b[ 0 ] - a[ 0 ] ) * t, a[ 1 ] + ( b[ 1 ] - a[ 1 ] ) * t ];
    fill( [ [ -0.28, -0.05 ], [ -0.52, -0.08 ], [ -0.54, 0.07 ], [ -0.28, 0.08 ] ], tone( [ 100, 92, 82 ] ) );
    for ( const side of [ -1, 1 ] ) {
      const sh = [ side * 0.12, -0.06 ], bk = [ side * 0.1, 0.12 ];
      const bend = [ side * 0.52, bendY ], end = [ side * tipX, tipY ], trail = [ side * 0.62, bendY + 0.28 ];
      fill( [ sh, bend, end, trail, bk ], tone( brown ) );
      fill( [ sh, bend, to( bend, trail, 0.35 ), to( sh, bk, 0.35 ) ], tone( silver ) );
      fill( [ to( bend, end, 0.5 ), end, to( trail, end, 0.5 ) ], tone( dark ) );
    }
    ctx.fillStyle = tone( [ 145, 136, 123 ] );
    ctx.beginPath(); ctx.ellipse( -0.02 * s, 0.04 * s, 0.36 * s, 0.15 * s, 0, 0, Math.PI * 2 ); ctx.fill();
    ctx.fillStyle = tone( head );
    ctx.beginPath(); ctx.arc( 0.2 * s, -0.08 * s, 0.12 * s, 0, Math.PI * 2 ); ctx.fill();
    fill( [ [ 0.24, -0.02 ], [ 0.66, 0.03 ], [ 0.3, 0.07 ] ], tone( pouch ) );
    fill( [ [ 0.22, -0.1 ], [ 0.78, 0 ], [ 0.74, 0.04 ], [ 0.22, -0.02 ] ], tone( bill ) );
  }

  /** The wallpaper: lines of pelicans scattered still down the page. */
  private drawWallPelicans( ctx: CanvasRenderingContext2D ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const r = this.seededLocal( 5 );
    const n = Math.max( 3, Math.round( W * H / 140000 ) );
    for ( let i = 0; i < n; i++ ) {
      const x0 = r() * W, y0 = r() * H, dir = r() < 0.5 ? 1 : -1, size = ( 14 + r() * 8 ) * k;
      const count = 2 + Math.floor( r() * 3 );
      for ( let b = 0; b < count; b++ ) {
        ctx.save();
        ctx.globalAlpha = 0.6 + r() * 0.3;
        ctx.translate( x0 - dir * b * size * 2.3, y0 + b * size * 0.25 );
        ctx.scale( dir, 1 );
        this.pelican( ctx, size, -0.05, -0.05 );
        ctx.restore();
      }
    }
    ctx.globalAlpha = 1;
  }

  /**
   * The still parts, painted once: the sky, its clouds, the city across the bay and the water with its
   * reflections on the back layer; the beach and its palms on the front one.
   */
  private bake( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const UW = this.UW;
    const hz = this.horizon;
    const cx = W / 2;
    const [ plum, pink, coral, teal, gold ] = this.five();
    const white: RGB = [ 255, 255, 255 ];
    const night: RGB = this.mix( plum, [ 14, 6, 14 ], 0.5 );
    const canvas = () => {
      const c = document.createElement( 'canvas' );
      c.width = Math.ceil( W * scale );
      c.height = Math.ceil( H * scale );
      const g = c.getContext( '2d' )!;
      g.scale( scale, scale );
      return { c, g };
    };
    this.neons = [];
    const sk = canvas();
    const { c, g } = canvas();
    // the sky: deep plum high up, so the words read, flamingo pink lower, orange and gold at the horizon
    const sky = g.createLinearGradient( 0, 0, 0, hz );
    sky.addColorStop( 0, this.rgba( night, 1 ) );
    sky.addColorStop( 0.45, this.rgba( this.mix( plum, pink, 0.45 ), 1 ) );
    sky.addColorStop( 0.8, this.rgba( this.mix( pink, coral, 0.55 ), 1 ) );
    sky.addColorStop( 1, this.rgba( this.mix( coral, gold, 0.45 ), 1 ) );
    sk.g.fillStyle = sky;
    sk.g.fillRect( 0, 0, W, hz + 1 );
    // the synthwave sun (owner): a great disc sinking behind the city in the middle, gold at its top
    // turning flamingo pink below, its lower half cut by level gaps that widen toward the horizon; a
    // wide glow round it
    // high enough that its striped half rises clear of the towers
    const sx = cx, sy = hz - H * 0.16 - 100, sR = H * 0.3;
    const sg = g.createRadialGradient( sx, sy, sR * 0.6, sx, sy, H * 0.75 );
    sg.addColorStop( 0, this.rgba( this.mix( gold, pink, 0.35 ), 0.55 ) );
    sg.addColorStop( 0.35, this.rgba( this.mix( pink, coral, 0.5 ), 0.22 ) );
    sg.addColorStop( 1, this.rgba( pink, 0 ) );
    sk.g.fillStyle = sg;
    sk.g.fillRect( 0, 0, W, hz + 1 );
    this.skyC = sk.c;
    this.sunGeo = { x: sx, y: sy, r: sR, scale };
    {
      const sun = canvas();
      const sgc = sun.g;
      const disc = sgc.createLinearGradient( 0, sy - sR, 0, sy + sR );
      disc.addColorStop( 0, this.rgba( this.mix( gold, white, 0.2 ), 1 ) );
      disc.addColorStop( 0.45, this.rgba( this.mix( gold, coral, 0.55 ), 1 ) );
      disc.addColorStop( 1, this.rgba( pink, 1 ) );
      sgc.fillStyle = disc;
      sgc.beginPath();
      sgc.arc( sx, sy, sR, 0, Math.PI * 2 );
      sgc.fill();
      // its gaps are cut as it is drawn, moving (drawSun)
      this.sunDisc = sun.c;
    }
    // level banks of cloud: long, soft, flat streaks, dark plum, lit pink and coral beneath where the
    // sun catches them
    const cr = this.seededLocal( 29 );
    const banks: [ number, number, number ][] = [ [ -0.5, 0.14, 0.7 ], [ 0.2, 0.09, 0.6 ], [ 0.5, 0.22, 0.6 ], [ -0.2, 0.36, 0.5 ], [ 0.35, 0.44, 0.55 ], [ -0.95, 0.3, 0.5 ], [ 1.0, 0.34, 0.5 ] ];
    for ( const [ u, v, bw ] of banks ) {
      const bx = cx + u * UW, by = H * v;
      for ( let i = 0; i < 26; i++ ) {
        const a = cr() * 2 - 1;
        const px = bx + a * bw * UW * 0.5, py = by + ( cr() - 0.5 ) * H * 0.025;
        const rx = ( 0.08 + cr() * 0.12 ) * UW * ( 1 - Math.abs( a ) * 0.5 ), ry = H * ( 0.008 + cr() * 0.014 );
        const lit = v / 0.45;
        const tone = this.mix( this.mix( plum, night, 0.2 ), this.mix( pink, coral, 0.45 ), Math.min( 0.85, 0.25 + 0.5 * lit ) );
        g.save();
        g.translate( px, py );
        g.scale( rx, ry );
        const pg = g.createRadialGradient( 0, 0.25, 0, 0, 0, 1 );
        pg.addColorStop( 0, this.rgba( this.mix( tone, gold, 0.12 * lit ), 0.55 ) );
        pg.addColorStop( 0.6, this.rgba( tone, 0.3 ) );
        pg.addColorStop( 1, this.rgba( tone, 0 ) );
        g.fillStyle = pg;
        g.fillRect( -1, -1, 2, 2 );
        g.restore();
        // the sun catching its underside: a thin brighter streak along its lower edge
        if ( cr() < 0.55 ) {
          g.save();
          g.translate( px, py + ry * 0.6 );
          g.scale( rx * 0.8, ry * 0.3 );
          const lg2 = g.createRadialGradient( 0, 0, 0, 0, 0, 1 );
          lg2.addColorStop( 0, this.rgba( this.mix( this.mix( pink, gold, 0.5 ), white, 0.15 ), 0.35 * Math.min( 1, lit ) ) );
          lg2.addColorStop( 1, this.rgba( pink, 0 ) );
          g.fillStyle = lg2;
          g.fillRect( -1, -1, 2, 2 );
          g.restore();
        }
      }
    }
    // a few first stars high in the plum
    const st = this.seededLocal( 91 );
    for ( let i = 0; i < 60; i++ ) {
      const x = st() * W, y = st() * H * 0.22;
      g.fillStyle = this.rgba( white, 0.25 + st() * 0.45 * ( 1 - y / ( H * 0.22 ) ) );
      g.fillRect( x, y, Math.max( 0.7, 0.9 * k ), Math.max( 0.7, 0.9 * k ) );
    }
    // the city across the bay (owner: more detail): Miami's towers dark against the sunset, the tallest
    // in the middle; each with its lit face and its shaded side, its own crown (flat, stepped back,
    // rounded, a spire), the faint level lines of its balconies, rows of windows lit warm and a few in
    // neon; lower blocks behind them, paler in the haze
    // a new city on every visit (owner), the tall towers always in the middle
    const tr = this.seededLocal( this.citySeed );
    type Tower = { x: number; w: number; h: number; kind: number; neon: number };
    const towers: Tower[] = [];
    for ( let u = -1.1; u < 1.1; ) {
      // 10% bigger, twice (owner)
      const w = UW * ( 0.013 + tr() * 0.02 ) * 1.1;
      const mid = Math.exp( -Math.pow( ( u - 0.02 ) / 0.38, 2 ) );
      // the middle ones, under the words, taller still (owner)
      // the shortest ones a little taller too (owner)
      const h = H * ( 0.055 + tr() * 0.05 + mid * ( 0.08 + tr() * 0.18 ) ) * 1.1;
      towers.push( { x: cx + u * UW, w, h, kind: Math.floor( tr() * 4 ), neon: tr() } );
      // close together (owner)
      u += w / UW * ( 0.8 + tr() * 0.7 );
    }
    // the antennas on three towers only (owner)
    {
      for ( const t of towers ) { if ( t.kind === 3 ) { t.kind = 0; } }
      // three, round the sun (owner): the tallest in the middle, one to its left, one to its right
      const uOf = ( t: { x: number; w: number } ) => ( t.x + t.w / 2 - cx ) / UW;
      for ( const [ lo, hi ] of [ [ -0.05, 0.05 ], [ -0.18, -0.06 ], [ 0.06, 0.18 ] ] ) {
        const pick = towers.filter( t => uOf( t ) >= lo && uOf( t ) <= hi && t.kind !== 3 ).sort( ( p2, q ) => q.h - p2.h )[ 0 ];
        if ( pick ) { pick.kind = 3; }
      }
    }
    // the tallest tower in the middle lit in pink neon (owner)
    {
      const central = towers.filter( t => Math.abs( ( t.x + t.w / 2 - cx ) / UW ) < 0.1 ).sort( ( p2, q ) => q.h - p2.h )[ 0 ];
      if ( central ) { central.neon = 0.01; }
    }
    const cityC = this.mix( this.mix( plum, [ 40, 20, 50 ], 0.45 ), coral, 0.1 );
    // the low blocks behind, in the haze
    for ( const t of towers ) {
      // taller (owner), so the far silhouette reads behind the towers
      const hh = t.h * 0.78, xx = t.x + t.w * 0.55;
      g.fillStyle = this.rgba( this.mix( cityC, this.mix( pink, coral, 0.5 ), 0.38 ), 1 );
      g.fillRect( xx, hz - hh, t.w * 1.1, hh + 1 );
    }
    // each tower its own colour (owner): Miami's pastels, sand, flamingo, mint, butter, sky, deepened
    // into the dusk; its windows in a real grid, glass a shade darker than its walls, a share of them lit
    // (some in ribbons across the floor, as the condos' glazed balconies are); and Miami's neon on many
    // of them: along the top, up a corner, round the crown, in a band
    const wr = this.seededLocal( this.citySeed + 76 );
    const pastels: RGB[] = [ [ 155, 148, 143 ], [ 153, 127, 134 ], [ 125, 148, 141 ], [ 155, 142, 109 ], [ 132, 141, 155 ], [ 160, 120, 150 ] ];
    const neonC: RGB[] = [ pink, teal, [ 146, 181, 232 ], gold ];
    for ( const t of towers ) {
      const { x, w, h } = t;
      // in perspective from across the bay: each tower shows the side that faces the middle, wider the
      // farther out it stands; backlit by the sun behind the city, its front in shade with a rim of light
      // along the edge toward the sun
      const uu = ( x + w / 2 - cx ) / UW;
      const side = w * ( 0.1 + Math.min( 0.4, Math.abs( uu ) * 0.6 ) );
      const top = hz - h;
      const fw = w - side;
      const leftSide = uu > 0;
      const fx = leftSide ? x + side : x;
      const sx2 = leftSide ? x : x + fw;
      const base = this.mix( this.mix( pastels[ Math.floor( wr() * pastels.length ) ], cityC, 0.55 ), coral, 0.05 );
      // the shaded side, the lit face
      g.fillStyle = this.rgba( this.mix( base, [ 20, 8, 22 ], 0.45 ), 1 );
      g.beginPath();
      // the side recedes: its far edge a little lower at the top
      g.moveTo( sx2, hz + 1 );
      g.lineTo( sx2, top + ( leftSide ? w * 0.06 : 0 ) );
      g.lineTo( sx2 + side, top + ( leftSide ? 0 : w * 0.06 ) );
      g.lineTo( sx2 + side, hz + 1 );
      g.closePath();
      g.fill();
      // the side's own windows, dimmer and squeezed
      if ( side > 2.5 ) {
        for ( let fy = top + 3.2 * k; fy < hz - 2 * k; fy += 3.2 * k ) {
          if ( wr() < 0.5 ) { g.fillStyle = this.rgba( this.mix( base, [ 255, 190, 90 ], 0.25 ), 0.5 ); g.fillRect( sx2 + side * 0.25, fy, side * 0.5, 1.4 * k ); }
          g.fillStyle = this.rgba( this.mix( base, [ 20, 8, 22 ], 0.45 ), 1 );
        }
      }
      const tg = g.createLinearGradient( 0, top, 0, hz );
      tg.addColorStop( 0, this.rgba( this.mix( base, this.mix( pink, gold, 0.4 ), 0.2 ), 1 ) );
      tg.addColorStop( 1, this.rgba( this.mix( base, [ 20, 8, 22 ], 0.2 ), 1 ) );
      g.fillStyle = tg;
      g.fillRect( fx, top, fw, h + 1 );
      // across the face, a shade lighter toward the sun
      const hg = g.createLinearGradient( fx, 0, fx + fw, 0 );
      const warmEdge = this.rgba( this.mix( pink, gold, 0.4 ), 0.16 );
      hg.addColorStop( leftSide ? 0 : 1, warmEdge );
      hg.addColorStop( leftSide ? 1 : 0, this.rgba( [ 20, 8, 22 ], 0.12 ) );
      g.fillStyle = hg;
      g.fillRect( fx, top, fw, h + 1 );
      // the rim of light along the edge toward the sun
      g.fillStyle = this.rgba( this.mix( gold, pink, 0.4 ), 0.55 );
      g.fillRect( leftSide ? sx2 : fx + fw - Math.max( 0.6, 0.7 * k ) + side, top, Math.max( 0.6, 0.7 * k ), h );
      // the crown
      g.fillStyle = this.rgba( this.mix( base, [ 20, 8, 22 ], 0.25 ), 1 );
      if ( t.kind === 1 ) {
        // a plain flat roof (owner: the stepped crowns are gone)
      } else if ( t.kind === 3 ) {
        // a tall slender mast, tapering, its small light at the top (owner: the new one, on two or three)
        // on a slender tower the mast is thinner and taller in step (owner)
        const slim = Math.max( 0, Math.min( 1, ( 14 * k - fw ) / ( 8 * k ) ) );
        const mx = fx + fw / 2, mh = h * 0.22 * ( 1 + 0.5 * slim );
        const bh2 = Math.max( 0.7, Math.min( 1.4 * k, fw * 0.09 ) );
        g.beginPath();
        g.moveTo( mx - bh2, top );
        g.lineTo( mx - 0.3, top - mh );
        g.lineTo( mx + 0.3, top - mh );
        g.lineTo( mx + bh2, top );
        g.closePath();
        g.fill();
        g.fillRect( mx - fw * 0.15, top - h * 0.02, fw * 0.3, h * 0.02 );
        g.save();
        g.shadowColor = this.rgba( pink, 0.9 );
        g.shadowBlur = 5 * k * scale;
        g.fillStyle = this.rgba( this.mix( pink, white, 0.3 ), 1 );
        g.beginPath(); g.arc( mx, top - mh, Math.max( 0.8, 1 * k ), 0, Math.PI * 2 ); g.fill();
        g.restore();
      }
      // the windows
      const floorH = 3.2 * k;
      const ribbon = wr() < 0.35;
      const litShare = 0.25 + wr() * 0.35;
      const glass = this.mix( base, [ 30, 20, 40 ], 0.45 );
      // the lit windows a golden hour yellow (owner), or, on a tower with neon, in its neon's colour
      const neonOn = t.neon < 0.5 && h > H * 0.06;
      const towerNeon = neonC[ Math.floor( t.neon * 8 ) % neonC.length ];
      const warm: RGB = neonOn ? this.mix( towerNeon, white, 0.2 ) : this.mix( gold, [ 255, 176, 72 ], 0.45 );
      // a neon tower's sign and lit windows are drawn as the scene runs, so they can change colour together
      const grp = neonOn ? { rects: [] as [ number, number, number, number ][], wins: [] as [ number, number, number, number ][], colour: towerNeon, from: towerNeon, to: towerNeon, fade: 1, next: 0 } : null;
      if ( grp ) { this.neons.push( grp ); }
      if ( fw > 5 ) {
        const cols = Math.max( 2, Math.floor( fw / ( 2.6 * k ) ) );
        const cw = fw / cols;
        for ( let fy = top + floorH; fy < hz - floorH * 0.6; fy += floorH ) {
          if ( ribbon ) {
            // a glazed band across the floor, its slab line pale above it
            g.fillStyle = this.rgba( glass, 0.9 );
            g.fillRect( fx + cw * 0.2, fy, fw - cw * 0.4, floorH * 0.5 );
            g.fillStyle = this.rgba( this.mix( base, white, 0.18 ), 0.6 );
            g.fillRect( fx, fy - Math.max( 0.5, 0.45 * k ), fw, Math.max( 0.5, 0.45 * k ) );
            for ( let c2 = 0; c2 < cols; c2++ ) {
              if ( wr() < litShare ) {
                const tint = wr();
                if ( grp ) { grp.wins.push( [ fx + c2 * cw + cw * 0.18, fy + 0.3 * k, cw * 0.64, floorH * 0.5 - 0.6 * k ] ); } else {
                  g.fillStyle = this.rgba( this.mix( warm, white, tint * 0.25 ), 0.85 );
                  g.fillRect( fx + c2 * cw + cw * 0.18, fy + 0.3 * k, cw * 0.64, floorH * 0.5 - 0.6 * k );
                }
              }
            }
          } else {
            for ( let c2 = 0; c2 < cols; c2++ ) {
              const wx = fx + c2 * cw + cw * 0.25, ww = cw * 0.5, wh = floorH * 0.5;
              const lit = wr() < litShare;
              if ( lit && grp ) {
                wr(); // (kept so the towers keep their colours)
                g.fillStyle = this.rgba( glass, 0.85 );
                g.fillRect( wx, fy, ww, wh );
                grp.wins.push( [ wx, fy, ww, wh ] );
              } else {
                g.fillStyle = lit ? this.rgba( this.mix( warm, white, wr() * 0.25 ), 0.88 ) : this.rgba( glass, 0.85 );
                g.fillRect( wx, fy, ww, wh );
              }
            }
          }
        }
      }
      // Miami's neon
      if ( neonOn ) {
        wr(); // (kept so the towers keep their colours)
        const nc = towerNeon;
        const style = Math.floor( wr() * 4 );
        void nc;
        const nr = ( a2: number, b2: number, c2: number, d2: number ) => { grp!.rects.push( [ a2, b2, c2, d2 ] ); };
        const lw = Math.max( 1, 1.1 * k );
        if ( style === 0 ) {
          nr( fx, top, fw, lw );
        } else if ( style === 3 ) {
          // more with the line along the top only, fewer outlined all round (owner)
          wr(); // (kept so the towers keep their colours)
          nr( fx, top, fw, lw );
        } else if ( style === 1 ) {
          // up both corners, all the way to the foot, and along the top (owner)
          nr( fx, top, lw, h );
          nr( fx + fw - lw, top, lw, h );
          nr( fx, top, fw, lw );
        } else if ( style === 2 ) {
          // no bands across the floors (owner): a line along the top instead
          wr(); // (kept so the towers keep their colours)
          nr( fx, top, fw, lw );
        }
      }
    }
    // the land the city stands on (owner): a low dark shore under the towers, with its trees and a few
    // lights along the water
    {
      // on a layer of its own, over the towers' lit windows and neon (owner: the greenery in front)
      const ln = canvas();
      const gL = ln.g;
      const lr = this.seededLocal( 101 );
      const landTop = ( x: number ) => hz - H * ( 0.006 + 0.003 * Math.sin( x * 0.02 ) + 0.002 * Math.sin( x * 0.07 ) );
      gL.beginPath();
      gL.moveTo( 0, hz + 1 );
      for ( let x = 0; x <= W; x += 3 ) { gL.lineTo( x, landTop( x ) ); }
      gL.lineTo( W, hz + 1 );
      gL.closePath();
      gL.fillStyle = this.rgba( this.mix( this.mix( night, teal, 0.08 ), plum, 0.2 ), 1 );
      gL.fill();
      for ( let x = 0; x < W; x += 3 * k ) {
        if ( lr() < 0.45 ) {
          gL.fillStyle = this.rgba( this.mix( night, [ 30, 60, 50 ], 0.25 ), 1 );
          gL.beginPath();
          gL.ellipse( x, landTop( x ) - 1 * k, ( 1.5 + lr() * 2.5 ) * k, ( 1 + lr() * 1.5 ) * k, 0, 0, Math.PI * 2 );
          gL.fill();
        }
        if ( lr() < 0.12 ) {
          gL.fillStyle = this.rgba( [ 255, 226, 154 ], 0.8 );
          gL.fillRect( x, hz - H * 0.003, Math.max( 0.7, 0.9 * k ), Math.max( 0.7, 0.9 * k ) );
        }
      }
      this.landFront = ln.c;
    }
    // the water: the sky mirrored, darker, paler toward the horizon
    const sea = g.createLinearGradient( 0, hz, 0, H );
    sea.addColorStop( 0, this.rgba( this.mix( this.mix( coral, gold, 0.4 ), plum, 0.25 ), 1 ) );
    sea.addColorStop( 0.35, this.rgba( this.mix( pink, plum, 0.45 ), 1 ) );
    sea.addColorStop( 1, this.rgba( this.mix( plum, night, 0.4 ), 1 ) );
    g.fillStyle = sea;
    g.fillRect( 0, hz, W, H - hz );
    // the reflections on their own layer, which the waves move (drawWaves)
    const rf = canvas();
    const gR = rf.g;
    // the towers' reflections: soft dark columns broken by the ripples
    for ( const { x, w, h } of towers ) {
      if ( h < H * 0.06 ) { continue; }
      const rh = h * 0.9;
      for ( let y = hz + 1; y < hz + rh; y += 2 ) {
        const f = ( y - hz ) / rh;
        const off = 0;
        gR.fillStyle = this.rgba( this.mix( cityC, plum, 0.3 ), 0.45 * ( 1 - f ) );
        gR.fillRect( x + off, y, w, 2.2 );
      }
    }
    // the sun's path on the water: soft level strokes of gold, close together near the horizon,
    // spreading toward us
    const pr2 = this.seededLocal( 43 );
    for ( let i = 0; i < 70; i++ ) {
      const d = Math.pow( pr2(), 1.4 );
      const y = hz + 2 + ( H - hz ) * d * 0.9;
      const spread = UW * ( 0.01 + 0.08 * d );
      const x = sx + ( pr2() - 0.5 ) * 2 * spread;
      gR.save();
      gR.translate( x, y );
      gR.scale( ( 8 + 40 * d ) * k * ( 0.6 + pr2() * 0.8 ), ( 0.8 + 1.6 * d ) * k );
      const lg = gR.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      lg.addColorStop( 0, this.rgba( this.mix( gold, white, 0.25 ), 0.45 * ( 1 - d * 0.6 ) ) );
      lg.addColorStop( 1, this.rgba( gold, 0 ) );
      gR.fillStyle = lg;
      gR.fillRect( -1, -1, 2, 2 );
      gR.restore();
    }
    // the ripples across the bay: faint level lines catching the sky, closer together far off
    const rr2 = this.seededLocal( 97 );
    for ( let i = 0; i < 140; i++ ) {
      const d = Math.pow( rr2(), 1.6 );
      const y = hz + 2 + ( H - hz ) * d;
      const len = ( 20 + 120 * d ) * k * ( 0.5 + rr2() );
      const x = rr2() * W;
      const lg3 = gR.createLinearGradient( x - len, 0, x + len, 0 );
      const c3 = this.mix( this.mix( pink, gold, rr2() * 0.6 ), white, 0.2 );
      lg3.addColorStop( 0, this.rgba( c3, 0 ) );
      lg3.addColorStop( 0.5, this.rgba( c3, 0.16 ) );
      lg3.addColorStop( 1, this.rgba( c3, 0 ) );
      gR.fillStyle = lg3;
      gR.fillRect( x - len, y, len * 2, Math.max( 0.6, ( 0.5 + 1.2 * d ) * k ) );
    }
    // the glints of the sunset on the water, steady, moved by the waves with the reflections (owner: not
    // blinking)
    {
      const gr2 = this.seededLocal( 61 );
      for ( let i = 0; i < 90; i++ ) {
        const d = Math.pow( gr2(), 1.3 );
        const x = gr2() * W, y = hz + ( H - hz ) * ( 0.02 + 0.96 * d );
        gr2(); gr2();
        const len = ( 6 + 26 * d ) * k;
        const c2 = [ pink, coral, gold ][ Math.floor( gr2() * 3 ) ];
        gR.save();
        gR.translate( x, y );
        gR.scale( len, Math.max( 0.6, len * 0.06 ) );
        const g2 = gR.createRadialGradient( 0, 0, 0, 0, 0, 1 );
        g2.addColorStop( 0, this.rgba( this.mix( c2, white, 0.3 ), 0.35 ) );
        g2.addColorStop( 1, this.rgba( c2, 0 ) );
        gR.fillStyle = g2;
        gR.fillRect( -1, -1, 2, 2 );
        gR.restore();
      }
    }
    this.refl = rf.c;
    // a soft clearing behind the hero's words, kept dark, so they read
    for ( const b of this.blocks ) {
      g.save();
      g.translate( b.x + b.w / 2, b.y + b.h / 2 );
      g.scale( b.w / 2 + 70 * k, b.h / 2 + 40 * k );
      const cl = g.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      cl.addColorStop( 0, this.rgba( night, 0.3 ) );
      cl.addColorStop( 1, this.rgba( night, 0 ) );
      g.fillStyle = cl;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    }
    void teal;
    this.back = c;

    // the front: the beach at the lower left, the sand catching the pink light, and its two palms
    const fr = canvas();
    const f = fr.g;
    const bx0 = cx - UW * 0.5;
    // a real beach (owner): wide, its waterline running from high at the left edge down to the bottom
    // well toward the middle, in a gentle curve
    // anchored at the stage's left edge, sized by height, reaching less far toward the middle (owner)
    const beachEnd = H * 0.55;
    this.beachEnd = beachEnd;
    const beachY = ( x: number ) => { const t = Math.max( 0, Math.min( 1.2, ( x - ( -10 ) ) / ( beachEnd + 10 ) ) ); return hz + ( H - hz ) * ( 0.32 + 0.72 * Math.pow( t, 1.4 ) ) - H * 0.012 * Math.sin( t * 5 ); };
    this.beachAt = beachY;
    f.beginPath();
    f.moveTo( -10, H + 2 );
    f.lineTo( -10, beachY( -10 ) );
    for ( let x = -10; x <= beachEnd; x += 3 ) { f.lineTo( x, beachY( x ) ); }
    f.lineTo( beachEnd, H + 2 );
    f.closePath();
    const sand = f.createLinearGradient( 0, hz + ( H - hz ) * 0.3, 0, H );
    // the sand lit warm by the sunset, deepening toward us
    sand.addColorStop( 0, this.rgba( this.mix( this.mix( coral, gold, 0.35 ), white, 0.12 ), 1 ) );
    sand.addColorStop( 0.5, this.rgba( this.mix( coral, plum, 0.3 ), 1 ) );
    sand.addColorStop( 1, this.rgba( this.mix( plum, night, 0.25 ), 1 ) );
    f.fillStyle = sand;
    f.fill();
    // the sand's texture: fine darker and lighter grains, a few shells
    const sr2 = this.seededLocal( 83 );
    for ( let i = 0; i < 700; i++ ) {
      const x = -10 + sr2() * ( beachEnd + 10 );
      const y0 = beachY( x );
      if ( !isFinite( y0 ) ) { continue; }
      const y = y0 + sr2() * ( H - y0 );
      f.fillStyle = this.rgba( sr2() < 0.5 ? this.mix( coral, white, 0.25 ) : this.mix( plum, night, 0.4 ), 0.25 );
      f.fillRect( x, y, Math.max( 0.6, 0.8 * k ), Math.max( 0.6, 0.8 * k ) );
    }
    // the wash: the thin sheet of the last wave on the sand, pale, its edge of foam broken into bubbles
    const washY = ( x: number ) => beachY( x ) + ( 5 + 3 * Math.sin( x * 0.05 ) ) * k;
    f.fillStyle = this.rgba( this.mix( pink, coral, 0.4 ), 0.25 );
    f.beginPath();
    for ( let x = -10; x <= beachEnd; x += 3 ) { x <= -10 ? f.moveTo( x, beachY( x ) ) : f.lineTo( x, beachY( x ) ); }
    for ( let x = beachEnd; x >= -10; x -= 3 ) { f.lineTo( x, washY( x ) ); }
    f.closePath();
    f.fill();
    f.strokeStyle = this.rgba( this.mix( pink, white, 0.45 ), 0.65 );
    f.lineWidth = Math.max( 1, 1.3 * k );
    f.beginPath();
    for ( let x = -10; x <= beachEnd; x += 3 ) { x <= -10 ? f.moveTo( x, washY( x ) ) : f.lineTo( x, washY( x ) ); }
    f.stroke();
    f.fillStyle = this.rgba( this.mix( pink, white, 0.5 ), 0.55 );
    for ( let x = -10; x <= beachEnd; x += 2.5 * k ) {
      if ( sr2() < 0.5 ) { f.beginPath(); f.arc( x, washY( x ) + sr2() * 2 * k, ( 0.4 + sr2() * 0.8 ) * k, 0, Math.PI * 2 ); f.fill(); }
    }
    // the grasses and the palms' trunks on a layer above the lapping waves (owner)
    const fr2 = canvas();
    const fOver = fr2.g;
    // the dune grasses along the top of the beach, in tufts, some with their seed heads
    const gr = this.seededLocal( 71 );
    fOver.strokeStyle = this.rgba( this.mix( night, teal, 0.12 ), 0.95 );
    for ( let i = 0; i < 40; i++ ) {
      const x0 = gr() * beachEnd * 0.6;
      const y0 = beachY( x0 ) + 6 * k + gr() * 14 * k;
      const blades = 6 + Math.floor( gr() * 8 );
      for ( let j = 0; j < blades; j++ ) {
        const hgt = ( 8 + gr() * 18 ) * k, lean = ( gr() - 0.5 ) * 1.2;
        fOver.lineWidth = Math.max( 0.5, ( 0.6 + gr() * 0.6 ) * k );
        fOver.beginPath();
        fOver.moveTo( x0 + ( gr() - 0.5 ) * 6 * k, y0 );
        fOver.quadraticCurveTo( x0 + lean * hgt * 0.3, y0 - hgt * 0.6, x0 + lean * hgt, y0 - hgt );
        fOver.stroke();
        if ( gr() < 0.12 ) {
          fOver.fillStyle = this.rgba( this.mix( night, coral, 0.2 ), 1 );
          fOver.beginPath(); fOver.ellipse( x0 + lean * hgt, y0 - hgt - 2 * k, 1 * k, 3 * k, lean * 0.5, 0, Math.PI * 2 ); fOver.fill();
        }
      }
    }
    // the palms at the far left, bigger and nearer (owner), leaning a little; each crown on its own layer
    this.crowns = [];
    // the shortest one gone (owner)
    // leaning further to the right (owner)
    const palms: [ number, number, number, number ][] = [ [ H * 0.06, H * 0.78, 0.32, 0 ], [ H * 0.22, H * 0.58, 0.24, 4 ] ];
    for ( const [ px, ph, lean, phase ] of palms ) {
      const crown = canvas();
      // standing a little lower on the sand (owner)
      const top = this.palm( fOver, crown.g, px, beachY( px ) + 14 * k + H * 0.11, ph, lean, night );
      this.crowns.push( { c: crown.c, px: top[ 0 ], py: top[ 1 ], phase } );
    }
    this.over = fr2.c;
    this.front = fr.c;
  }

  /**
   * A palm, a silhouette against the sunset: its slender trunk curving up and leaning, ringed, narrowing
   * a little; its crown of long fronds arching out and drooping, each a spine with its leaflets hanging
   * from it. `x, y` its foot, `h` its height, `lean` how far it leans (a share of its height).
   */
  private palm( fIn: CanvasRenderingContext2D, cg: CanvasRenderingContext2D, x: number, y: number, h: number, lean: number, c: RGB ): [ number, number ] {
    const k = this.scaleK;
    const tx = x + h * lean, ty = y - h;
    let f = fIn;
    // thicker for its size, as a palm near us is
    const bw = h * 0.032, tw = h * 0.02;
    f.fillStyle = this.rgba( c, 1 );
    f.beginPath();
    f.moveTo( x - bw / 2, y );
    f.quadraticCurveTo( x + h * lean * 0.2 - bw / 2, y - h * 0.6, tx - tw / 2, ty );
    f.lineTo( tx + tw / 2, ty );
    f.quadraticCurveTo( x + h * lean * 0.2 + bw / 2, y - h * 0.6, x + bw / 2, y );
    f.closePath();
    f.fill();
    // the trunk's rings, and the sunset's rim of light down its side
    const [ , pink, coral ] = this.five();
    const at = ( t: number ): [ number, number ] => {
      const mx = x + h * lean * 0.2, my = y - h * 0.6;
      const u2 = 1 - t;
      return [ u2 * u2 * x + 2 * u2 * t * mx + t * t * tx, u2 * u2 * y + 2 * u2 * t * my + t * t * ty ];
    };
    for ( let t = 0.03; t < 0.97; t += 0.035 ) {
      const [ px, py ] = at( t );
      const ww = bw + ( tw - bw ) * t;
      f.strokeStyle = this.rgba( this.mix( c, [ 0, 0, 0 ], 0.35 ), 0.9 );
      f.lineWidth = Math.max( 0.5, 0.7 * k );
      f.beginPath(); f.moveTo( px - ww / 2, py ); f.lineTo( px + ww / 2, py + ww * 0.15 ); f.stroke();
    }
    f.strokeStyle = this.rgba( this.mix( pink, coral, 0.5 ), 0.5 );
    f.lineWidth = Math.max( 0.6, 0.9 * k );
    f.beginPath();
    for ( let t = 0; t <= 1; t += 0.05 ) { const [ px, py ] = at( t ); const ww = bw + ( tw - bw ) * t; t === 0 ? f.moveTo( px + ww / 2 - 0.5, py ) : f.lineTo( px + ww / 2 - 0.5, py ); }
    f.stroke();
    // from here on, the crown, on its own layer
    const trunkF = f;
    void trunkF;
    f = cg;
    // a cluster of coconuts under the crown
    f.fillStyle = this.rgba( this.mix( c, coral, 0.12 ), 1 );
    for ( const [ ox, oy ] of [ [ -3, 4 ], [ 2, 5 ], [ -0.5, 7.5 ] ] ) {
      f.beginPath(); f.arc( tx + ox * k, ty + oy * k, 2.4 * k, 0, Math.PI * 2 ); f.fill();
    }
    // the fronds, drawn with care (owner: as Sakura's and Autumn's leaves): each a smooth arching
    // spine that droops toward its tip, its leaflets slender curved blades hanging from both sides,
    // longest at the middle of the frond, each with a faint rim of the sunset along its upper edge
    const [ , rimPink, rimCoral ] = this.five();
    const rim = this.mix( rimPink, rimCoral, 0.5 );
    const r = this.seededLocal( Math.round( x ) + 3 );
    const fronds = 13;
    for ( let i = 0; i < fronds; i++ ) {
      const a = -Math.PI / 2 + ( i / ( fronds - 1 ) - 0.5 ) * Math.PI * 1.8 + ( r() - 0.5 ) * 0.18;
      const len = h * ( 0.3 + r() * 0.08 );
      const droop = 0.45 + r() * 0.35;
      const at = ( t: number ): [ number, number ] => [ tx + Math.cos( a ) * len * t, ty + Math.sin( a ) * len * t * 0.75 + len * droop * t * t * 0.95 ];
      // the spine, tapering
      f.strokeStyle = this.rgba( c, 1 );
      f.lineCap = 'round';
      for ( let q = 0; q < 12; q++ ) {
        const [ ax, ay ] = at( q / 12 ), [ bx2, by2 ] = at( ( q + 1 ) / 12 );
        f.lineWidth = Math.max( 0.6, 2.2 * k * ( 1 - q / 12 ) + 0.4 );
        f.beginPath(); f.moveTo( ax, ay ); f.lineTo( bx2, by2 ); f.stroke();
      }
      // the leaflets
      const nL = 28;
      for ( let q = 1; q <= nL; q++ ) {
        const t = q / ( nL + 1 );
        const [ px, py ] = at( t );
        const [ qx, qy ] = at( Math.min( 1, t + 0.02 ) );
        const dir = Math.atan2( qy - py, qx - px );
        const ll = len * 0.3 * Math.sin( Math.PI * Math.min( 1, t * 1.05 ) ) * ( 0.85 + r() * 0.3 );
        for ( const sd of [ -1, 1 ] ) {
          // out from the spine at an angle, then bending down under its weight
          const out = dir + sd * ( 0.9 + r() * 0.3 );
          const tipX = px + Math.cos( out ) * ll * 0.75;
          const tipY = py + Math.sin( out ) * ll * 0.75 + ll * 0.55;
          const cxp = px + Math.cos( out ) * ll * 0.55, cyp = py + Math.sin( out ) * ll * 0.55;
          const wv = ll * 0.15;
          const nx = Math.cos( out + Math.PI / 2 ) * wv, ny = Math.sin( out + Math.PI / 2 ) * wv;
          f.fillStyle = this.rgba( c, 1 );
          f.beginPath();
          f.moveTo( px, py );
          f.quadraticCurveTo( cxp + nx, cyp + ny, tipX, tipY );
          f.quadraticCurveTo( cxp - nx, cyp - ny, px, py );
          f.fill();
          // its rim of light along the upper edge, faint
          f.strokeStyle = this.rgba( rim, 0.18 );
          f.lineWidth = Math.max( 0.4, 0.5 * k );
          f.beginPath();
          f.moveTo( px, py );
          f.quadraticCurveTo( cxp - nx, cyp - ny, tipX, tipY );
          f.stroke();
        }
      }
    }
    // the knot of the crown where the fronds meet
    f.beginPath();
    f.ellipse( tx, ty + 2 * k, h * 0.018, h * 0.012, 0, 0, Math.PI * 2 );
    f.fill();
    return [ tx, ty ];
  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The colour circle's five, or Miami's own: plum, flamingo pink, coral, teal, gold. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 83, 32, 51 ], [ 255, 95, 162 ], [ 254, 128, 120 ], [ 0, 209, 193 ], [ 248, 210, 115 ] ];
  }
}
